/**
 * Escáner de ticket — la magia.
 *
 * Tres estados:
 *   idle      → drop/foto, nota RGPD "no la guardamos"
 *   scanning  → línea de escáner animada sobre skeleton + 3 puntos pulsando
 *               (sin spinner)
 *   result    → cabecera local + total, lista de platos con chip "¿de quién?"
 *               + totales por comensal coloreados.
 *
 * Mantiene la lógica de datos (endpoints, formato de assignments, callbacks).
 */
import { useRef, useState } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';


interface Item {
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface ScanResult {
  id: string;
  vendor_name: string | null;
  total_amount: number | null;
  items: Item[];
  ocr_success: boolean;
  error?: string;
}

interface Props {
  slug: string;
  adminToken: string;
  onSplitDone?: () => void;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

// 6 colores para los comensales, robados de la paleta andaluza.
const PERSON_PALETTE = [
  { dot: '#1C5D82', text: '#154B6B', bg: '#E6EEF3' },
  { dot: '#C2683E', text: '#A8542F', bg: '#F6E6DB' },
  { dot: '#5E7B3E', text: '#46602C', bg: '#E8EEDC' },
  { dot: '#BE841B', text: '#8A5F12', bg: '#F7EBD2' },
  { dot: '#15506B', text: '#103B50', bg: '#E2EBF0' },
  { dot: '#B0392C', text: '#8A2C22', bg: '#F6E0DC' },
];
function personColor(i: number) {
  return PERSON_PALETTE[i % PERSON_PALETTE.length];
}


export default function ReceiptScanner({ slug, adminToken, onSplitDone }: Props) {
  const [stage, setStage] = useState<'idle' | 'scanning' | 'result' | 'submitting'>('idle');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [people, setPeople] = useState<string[]>(['']);
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setStage('scanning');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(
        `/public/group-buys/admin/${slug}/receipt/scan?t=${adminToken}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(data);
      setAssignments({});
      setStage('result');
      if (!data.ocr_success) {
        toast.error('No hemos podido leer el ticket bien. Prueba con otra foto.');
      } else if (data.ocr_daily_limit && data.ocr_used_today >= data.ocr_daily_limit - 1) {
        toast(`Te queda ${data.ocr_daily_limit - data.ocr_used_today} OCR hoy en este cobro`, {
          icon: '⚠️',
        });
      }
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 422) {
        toast.error(detail || 'Configura el cobro antes de escanear (importe y comensales)', {
          duration: 6000,
        });
      } else if (status === 429) {
        toast.error(detail || 'Has llegado al límite diario de OCRs en este cobro', {
          duration: 8000,
        });
      } else {
        toast.error(detail || 'Error subiendo la foto');
      }
      setStage('idle');
    }
  };

  const addPerson = () => setPeople([...people, '']);
  const setPersonName = (i: number, name: string) => {
    const next = [...people];
    next[i] = name;
    setPeople(next);
  };
  const removePerson = (i: number) => {
    setPeople(people.filter((_, idx) => idx !== i));
    setAssignments(
      Object.fromEntries(
        Object.entries(assignments)
          .filter(([, p]) => p !== i)
          .map(([item, p]) => [item, Number(p) > i ? Number(p) - 1 : Number(p)]),
      ),
    );
  };

  const assignItem = (itemIdx: number, personIdx: number) => {
    setAssignments({ ...assignments, [itemIdx]: personIdx });
  };
  const clearAssignment = (itemIdx: number) => {
    setAssignments(
      Object.fromEntries(
        Object.entries(assignments).filter(([k]) => Number(k) !== itemIdx),
      ),
    );
  };

  const peoplePaidPerson = (personIdx: number): number => {
    if (!result) return 0;
    let total = 0;
    Object.entries(assignments).forEach(([itemIdx, p]) => {
      if (p === personIdx) total += result.items[Number(itemIdx)]?.line_total || 0;
    });
    return total;
  };

  const unassignedCount = result
    ? result.items.length - Object.keys(assignments).length
    : 0;

  const submit = async () => {
    if (!result || !result.id) return;
    const validPeople = people.filter((n) => n.trim());
    if (validPeople.length === 0) {
      toast.error('Pon al menos una persona');
      return;
    }
    if (Object.keys(assignments).length === 0) {
      toast.error('Asigna al menos un item a una persona');
      return;
    }

    const dict: Record<string, number[]> = {};
    validPeople.forEach((name, idx) => {
      dict[name] = [];
      Object.entries(assignments).forEach(([itemIdx, p]) => {
        if (Number(p) === idx) dict[name].push(Number(itemIdx));
      });
    });

    setStage('submitting');
    try {
      await api.post(
        `/public/group-buys/admin/${slug}/receipt/${result.id}/split-uneven?t=${adminToken}`,
        { assignments: dict },
      );
      toast.success('Participantes creados con sus importes');
      setStage('idle');
      setResult(null);
      setPeople(['']);
      setAssignments({});
      onSplitDone?.();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error creando participantes');
      setStage('result');
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) upload(e.dataTransfer.files[0]);
  };

  return (
    <div className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra-700 mb-3">
        · La magia · escáner de ticket
      </div>
      <h2 className="font-display font-semibold text-[22px] text-ink tracking-[-0.01em] mb-1">
        Dividir desde la foto del ticket
      </h2>
      <p className="text-[13.5px] text-ink-700 mb-6">
        Sube una foto. Extraemos los items con IA y divides por quién pidió qué.
      </p>

      {stage === 'idle' && (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="block border-[1.5px] border-dashed border-line-strong rounded-xl bg-surface px-6 py-10 text-center cursor-pointer hover:border-terra hover:bg-terra-50 transition"
        >
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden"
          />
          <div
            className="mx-auto mb-4 w-12 h-12 rounded-xl"
            style={{ background: '#F6E6DB', border: '1.5px solid #E6CDB6' }}
            aria-hidden
          />
          <p className="font-display font-semibold text-[18px] text-ink tracking-[-0.01em] mb-1">
            Toca aquí o suelta una foto
          </p>
          <p className="text-[13px] text-ink-700">
            Reconocemos lo que pidió cada uno y calculamos su parte.
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">
            · No guardamos la foto. La leemos y la borramos.
          </p>
        </label>
      )}

      {stage === 'scanning' && <Scanning />}

      {stage === 'submitting' && <Scanning label="Creando participantes…" />}

      {stage === 'result' && result && (
        <div className="space-y-6">
          {/* Cabecera del ticket */}
          <div className="bg-surface border border-line rounded-xl p-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
                · Local detectado
              </div>
              <p className="font-display font-semibold text-[18px] text-ink tracking-[-0.01em]">
                {result.vendor_name || 'Ticket sin nombre'}
              </p>
              <p className="text-[12.5px] text-ink-500 mt-0.5">
                {result.items.length} items detectados
              </p>
            </div>
            {result.total_amount && (
              <div className="text-right">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
                  · Total
                </div>
                <p className="font-display font-bold text-[24px] text-olive-700 leading-none">
                  {formatEUR(result.total_amount)}
                </p>
              </div>
            )}
          </div>

          {/* Comensales */}
          <div>
            <label className="cc-label">¿Quién paga?</label>
            <div className="space-y-2">
              {people.map((name, idx) => {
                const col = personColor(idx);
                const paid = peoplePaidPerson(idx);
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: col.dot }}
                      aria-hidden
                    />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setPersonName(idx, e.target.value)}
                      placeholder={`Comensal ${idx + 1}`}
                      className="cc-input"
                    />
                    {name.trim() && (
                      <div
                        className="px-2.5 py-1.5 rounded-pill font-display font-semibold text-[13.5px] shrink-0 min-w-[78px] text-center"
                        style={{ background: col.bg, color: col.text }}
                      >
                        {formatEUR(paid)}
                      </div>
                    )}
                    {people.length > 1 && (
                      <button
                        onClick={() => removePerson(idx)}
                        className="text-ink-400 hover:text-brick px-2 text-[18px] leading-none"
                        type="button"
                        aria-label={`Quitar ${name || `comensal ${idx + 1}`}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
              <button
                onClick={addPerson}
                type="button"
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700 transition"
              >
                + Añadir comensal
              </button>
            </div>
          </div>

          {/* Items para asignar */}
          {result.items.length > 0 ? (
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="cc-label !mb-0">Platos</label>
                {unassignedCount > 0 ? (
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-amberCC-700">
                    · {unassignedCount} sin asignar
                  </span>
                ) : (
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-olive-700">
                    · todo asignado
                  </span>
                )}
              </div>
              <ul className="divide-y divide-line-soft border border-line rounded-xl bg-paper overflow-hidden">
                {result.items.map((item, idx) => {
                  const assigned = assignments[idx];
                  const col = assigned != null ? personColor(assigned) : null;
                  return (
                    <li
                      key={idx}
                      className="flex items-center gap-3 px-4 py-3"
                      style={col ? { background: col.bg } : undefined}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold text-[15px] text-ink tracking-[-0.01em] truncate">
                          {item.quantity > 1 && `${item.quantity}× `}
                          {item.name}
                        </p>
                        <p className="text-[12.5px] text-ink-500">
                          {formatEUR(item.line_total)}
                        </p>
                      </div>
                      {assigned != null ? (
                        <button
                          type="button"
                          onClick={() => clearAssignment(idx)}
                          className="px-3 py-1.5 rounded-pill font-semibold text-[12px] inline-flex items-center gap-1.5"
                          style={col ? { background: col.dot, color: '#FFFFFF' } : undefined}
                          aria-label={`Quitar ${people[assigned] || `comensal ${assigned + 1}`}`}
                        >
                          {people[assigned] || `Comensal ${assigned + 1}`}
                          <span className="opacity-80">×</span>
                        </button>
                      ) : (
                        <select
                          value=""
                          onChange={(e) =>
                            e.target.value !== '' && assignItem(idx, Number(e.target.value))
                          }
                          className="text-[13px] border-[1.5px] border-line-strong rounded-pill px-3 py-1.5 bg-paper text-ink-700 focus:border-brand outline-none"
                        >
                          <option value="">¿de quién?</option>
                          {people.map(
                            (name, i) =>
                              name.trim() && (
                                <option key={i} value={i}>
                                  {name}
                                </option>
                              ),
                          )}
                        </select>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="bg-amberCC-50 border border-[#ECD9AE] rounded-xl p-4 text-[13.5px] text-amberCC-700">
              No hemos podido extraer items individuales. Puedes seguir creando
              participantes a mano desde el panel.
            </div>
          )}

          {/* Acciones */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setStage('idle');
                setResult(null);
              }}
              className="px-5 py-3 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 font-semibold text-[14px] hover:border-brand transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={result.items.length === 0 || Object.keys(assignments).length === 0}
              className="flex-1 px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14.5px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed"
            >
              Crear participantes con sus importes →
            </button>
          </div>
        </div>
      )}

      <Style />
    </div>
  );
}


function Scanning({ label = 'Leyendo el ticket…' }: { label?: string }) {
  return (
    <div className="py-2">
      {/* Marco con skeleton + línea de escaner */}
      <div className="relative mx-auto max-w-[280px] aspect-[3/4] rounded-xl overflow-hidden border border-line bg-surface">
        {/* Skeleton "papel del ticket" */}
        <div className="absolute inset-0 p-4 space-y-2">
          <div className="cc-skel h-3 w-3/4" />
          <div className="cc-skel h-2 w-1/2" />
          <div className="h-3" />
          <div className="cc-skel h-2.5 w-full" />
          <div className="cc-skel h-2.5 w-5/6" />
          <div className="cc-skel h-2.5 w-3/4" />
          <div className="cc-skel h-2.5 w-2/3" />
          <div className="cc-skel h-2.5 w-4/5" />
          <div className="cc-skel h-2.5 w-1/2" />
          <div className="h-3" />
          <div className="cc-skel h-3 w-full" />
          <div className="cc-skel h-3 w-2/3" />
        </div>
        {/* Línea de escáner */}
        <div className="cc-scanline" aria-hidden />
      </div>

      {/* 3 puntos pulsando */}
      <div className="flex items-center justify-center gap-1.5 mt-5 mb-2">
        <span
          className="w-1.5 h-1.5 rounded-full bg-terra"
          style={{ animation: 'cc-pulse 1.4s ease-in-out infinite', animationDelay: '0s' }}
        />
        <span
          className="w-1.5 h-1.5 rounded-full bg-terra"
          style={{ animation: 'cc-pulse 1.4s ease-in-out infinite', animationDelay: '.2s' }}
        />
        <span
          className="w-1.5 h-1.5 rounded-full bg-terra"
          style={{ animation: 'cc-pulse 1.4s ease-in-out infinite', animationDelay: '.4s' }}
        />
      </div>
      <p className="text-center text-[13.5px] text-ink-700">{label}</p>
      <p className="text-center font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mt-1">
        · Unos 3-5 segundos
      </p>
    </div>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
      .cc-label{
        display:block;
        font-family:"Hanken Grotesk",sans-serif;
        font-size:12.5px;
        font-weight:700;
        letter-spacing:.02em;
        color:#4A4239;
        margin-bottom:7px;
      }
      .cc-skel{
        background: linear-gradient(90deg,#EAE1D2 0%,#F4ECDF 50%,#EAE1D2 100%);
        background-size: 200% 100%;
        animation: cc-shimmer 1.3s linear infinite;
        border-radius: 4px;
      }
      .cc-scanline{
        position:absolute; left:0; right:0; top:0; height:2px;
        background: linear-gradient(90deg, transparent 0%, #C2683E 50%, transparent 100%);
        box-shadow: 0 0 14px 2px rgba(194,104,62,.55);
        animation: cc-scan 2.1s ease-in-out infinite;
      }
      @keyframes cc-shimmer{
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes cc-scan{
        0%   { transform: translateY(0); }
        50%  { transform: translateY(100%); }
        100% { transform: translateY(0); }
      }
      @keyframes cc-pulse{
        0%, 80%, 100% { opacity: .3; transform: scale(.85); }
        40% { opacity: 1; transform: scale(1); }
      }
    `}</style>
  );
}
